import { Button, Card, Col, DatePicker, Empty, message, Row, Select, Table } from 'antd';
import gql from 'graphql-tag';
import * as moment from 'moment';
import React, { Component } from 'react';
import { Mutation, Query } from 'react-apollo';

import { cancelClockInMutation, doClockInMutation } from '../../mutations/clockIn';
import { DateRange } from '../../types/base';
import {
  ActivityRewardStatus,
  GClockInActivityJoinedRecordWithStatus,
  MarketingActivity,
} from '../../types/marketingActivity';
import { ClockInRewardStatus, ClockInRewardStatusText } from '../group/GroupDetail';

export const USER_CLOCKIN_JOINED_RECORDS_QUERY = gql`
  query GetUserClockInRecords($ids: [ID], $activityId: ID) {
    users(ids: $ids) {
      clockInActivityJoinedRecords(activityId: $activityId) {
        sourceId
        quantity
        status
        scheduledTime
        clockedInAt
        createdAt
      }
    }
  }
`;

interface Props {
  userId: number;
  marketingActivityFilterOptions: MarketingActivity[];
}

interface State {
  clockInDateRange: DateRange;
  selectedMarketingActivityFilter: number;
}

export class ClockInRecords extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      clockInDateRange: {
        from: moment().subtract(6, 'days').toDate(),
        to: new Date(),
      },
      selectedMarketingActivityFilter: 0,
    };
  }

  getClockInButton(userId: number, date: Date, clockInRecord?: GClockInActivityJoinedRecordWithStatus) {
    const now = moment().startOf('day');
    const { marketingActivityFilterOptions } = this.props;
    const { selectedMarketingActivityFilter } = this.state;
    const activityId = marketingActivityFilterOptions[selectedMarketingActivityFilter].id;
    const ids = [Number(userId)];
    const clockInAt = moment(date).hour(17).minute(0).second(0).millisecond(0);

    if (!clockInRecord) {
      return (
        <Mutation
          mutation={doClockInMutation}
          variables={{ activityId, userId, date: clockInAt }}
          refetchQueries={[
            {
              query: USER_CLOCKIN_JOINED_RECORDS_QUERY,
              variables: { ids, activityId },
            },
          ]}
        >
          {(doClockIn, { loading, error }) => {
            let buttonText = '确认打卡';
            if (clockInAt.isBefore(now)) {
              buttonText = '补打卡';
            }
            return (
              <Button
                loading={loading}
                size="small"
                type="primary"
                onClick={async () => {
                  try {
                    await doClockIn();
                    message.success('打卡成功！');
                  } catch {
                    message.error('打卡失败！');
                  }
                }}
              >
                {buttonText}
              </Button>
            );
          }}
        </Mutation>
      );
    }

    return (
      <Mutation
        mutation={cancelClockInMutation}
        variables={{ activityId, id: clockInRecord && clockInRecord.sourceId }}
        refetchQueries={[
          {
            query: USER_CLOCKIN_JOINED_RECORDS_QUERY,
            variables: { ids, activityId },
          },
        ]}
      >
        {(cancelClockIn, { loading }) => {
          return (
            <Button
              loading={loading}
              size="small"
              type="danger"
              onClick={async () => {
                await cancelClockIn();
              }}
            >
              取消打卡
            </Button>
          );
        }}
      </Mutation>
    );
  }

  getClockInList(userId: number, clockInRecords: GClockInActivityJoinedRecordWithStatus[], loading: boolean) {
    const { clockInDateRange } = this.state;

    const clockInRecordsByDate: {
      date: Date;
      clockInRecord?: GClockInActivityJoinedRecordWithStatus;
    }[] = [];

    const from = moment(clockInDateRange.from);
    const to = moment(clockInDateRange.to);

    do {
      const clockInRecord = clockInRecords.find(clockInRecord => from.isSame(clockInRecord.clockedInAt, 'day'));
      clockInRecordsByDate.push({ clockInRecord, date: from.toDate() });
      from.add(1, 'day');
    } while (from.isBefore(to));

    return (
      <Table<{
        date: Date;
        clockInRecord?: GClockInActivityJoinedRecordWithStatus;
      }>
        columns={[
          {
            title: '日期',
            key: 'date',
            dataIndex: 'date',
            render: date => moment(date).format('YYYY.MM.DD'),
          },
          {
            title: '积分状态',
            key: 'status',
            render: record => {
              const { clockInRecord } = record;
              if (clockInRecord) {
                if (clockInRecord.status === ActivityRewardStatus.SCHEDULED) {
                  return ClockInRewardStatusText[ClockInRewardStatus.CLOCKED_IN];
                }
                return ClockInRewardStatusText[ClockInRewardStatus.SCORE_SENT];
              }

              return ClockInRewardStatusText[ClockInRewardStatus.NONE_CLOCKED_IN];
            },
          },
          {
            title: '积分数量',
            key: 'quantity',
            render: record => (record.clockInRecord && record.clockInRecord.quantity) || 0,
          },
          {
            title: '来源',
            key: 'from',
            render: () => '打卡',
          },
          {
            title: '操作',
            key: 'operation',
            render: record => this.getClockInButton(userId, record.date, record.clockInRecord),
          },
        ]}
        loading={loading}
        dataSource={clockInRecordsByDate}
        rowKey={record => `${record.date}`}
        pagination={false}
      />
    );
  }
  getClockInSentPoint(clockInActivityJoinedRecords: GClockInActivityJoinedRecordWithStatus[]) {
    let enrnedPoint = 0;
    for (const activityJoinedRecord of clockInActivityJoinedRecords) {
      if (activityJoinedRecord.status === ActivityRewardStatus.SENT) {
        enrnedPoint += Number(activityJoinedRecord.quantity);
      }
    }
    return enrnedPoint;
  }
  getClockInPendingPoint(clockInActivityJoinedRecords: GClockInActivityJoinedRecordWithStatus[]) {
    let pendingPoint = 0;
    for (const activityJoinedRecord of clockInActivityJoinedRecords) {
      if (activityJoinedRecord.status === ActivityRewardStatus.SCHEDULED) {
        pendingPoint += Number(activityJoinedRecord.quantity);
      }
    }
    return pendingPoint;
  }
  render() {
    const { userId, marketingActivityFilterOptions } = this.props;
    const { selectedMarketingActivityFilter, clockInDateRange } = this.state;
    if (!marketingActivityFilterOptions.length) {
      return null;
    }
    const ids = [Number(userId)];
    const activityId = marketingActivityFilterOptions[selectedMarketingActivityFilter].id;
    return (
      <Query query={USER_CLOCKIN_JOINED_RECORDS_QUERY} variables={{ ids, activityId }}>
        {({ loading, error, data }) => {
          if (error) return <div>{error.message}</div>;
          if (loading) return <div>loading...</div>;
          if (!data) {
            // @ts-ignore
            return <Empty />;
          }
          const [user] = data.users || {};
          const clockInActivityJoinedRecords = (user && user.clockInActivityJoinedRecords) || [];
          return (
            <div>
              <Card bordered={false}>
                <Row gutter={24}>
                  <Col span={4} style={{ textAlign: 'right' }}>
                    累计打卡天数
                  </Col>
                  <Col span={4}>{clockInActivityJoinedRecords.length}</Col>

                  <Col span={4} style={{ textAlign: 'right' }}>
                    打卡待发积分
                  </Col>
                  <Col span={4}>{this.getClockInSentPoint(clockInActivityJoinedRecords)}</Col>

                  <Col span={4} style={{ textAlign: 'right' }}>
                    打卡已发积分
                  </Col>
                  <Col span={4}>{this.getClockInPendingPoint(clockInActivityJoinedRecords)}</Col>
                </Row>
                <Row gutter={24} style={{ marginTop: 10 }}>
                  <Col span={5} style={{ textAlign: 'right' }}>
                    打卡活动
                  </Col>
                  <Col span={19}>
                    <Select
                      style={{ width: '300px' }}
                      defaultValue={marketingActivityFilterOptions[selectedMarketingActivityFilter].name}
                      onChange={value => {
                        this.setState({
                          selectedMarketingActivityFilter: Number(value),
                        });
                      }}
                    >
                      {marketingActivityFilterOptions.map((option, index) => (
                        <Select.Option value={index} key={`${index}`}>
                          {option.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>
                </Row>
                <Row gutter={24} style={{ marginTop: 10 }}>
                  <Col span={5} style={{ textAlign: 'right' }}>
                    筛选时间
                  </Col>
                  <Col span={19}>
                    <DatePicker.RangePicker
                      value={[moment(clockInDateRange.from), moment(clockInDateRange.to)]}
                      ranges={{
                        今天: [moment(), moment()],
                        本周: [moment().startOf('week'), moment().endOf('week')],
                        上周: [
                          moment().startOf('week').subtract(1, 'week'),
                          moment().endOf('week').subtract(1, 'week'),
                        ],
                      }}
                      onChange={dates =>
                        this.setState({
                          clockInDateRange: {
                            from: moment(dates[0]).startOf('day').toDate(),
                            to: moment(dates[1]).endOf('day').toDate(),
                          },
                        })
                      }
                    />
                  </Col>
                </Row>
              </Card>
              {this.getClockInList(userId, clockInActivityJoinedRecords, loading)}
            </div>
          );
        }}
      </Query>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/user/ClockInRecords.tsx